import styled from 'styled-components';
import { Link } from 'react-router-dom';

export const Container = styled.div`
  width: min(1120px, calc(100% - 96px));
  margin-inline: auto;
  @media (max-width: 700px) {
    width: calc(100% - 40px);
  }
`;
export const Header = styled.header`
  background: var(--bg);
  border-bottom: 1px solid var(--rule);
  ${Container} {
    min-height: 84px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
  }
  nav {
    display: flex;
    align-items: center;
    gap: 28px;
  }
  a {
    min-height: 44px;
    display: inline-flex;
    align-items: center;
    font-size: 13px;
    color: var(--ink);
  }
  a:hover {
    color: var(--accent);
  }
  @media (max-width: 700px) {
    ${Container} {
      min-height: 72px;
    }
    nav {
      gap: 18px;
    }
    nav a {
      font-size: 12px;
    }
  }
`;
export const Brand = styled(Link)`
  font-weight: 600;
  letter-spacing: -0.04em;
  font-size: 21px !important;
`;
export const Kicker = styled.p`
  font-family: var(--font-mono);
  font-size: 11px;
  line-height: 1.6;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  color: var(--ink-mute);
`;
export const Hero = styled.section`
  padding: 86px 0 56px;
  h1 {
    margin: 22px 0 24px;
    font-family: var(--font-body);
    font-size: clamp(44px, 5vw, 67px);
    font-weight: 600;
    line-height: 1.07;
    letter-spacing: -0.045em;
    color: var(--ink);
  }
  h1 em {
    font-weight: 600;
    font-style: normal;
    color: #5b4ac7;
  }
  @media (max-width: 700px) {
    padding: 50px 0 36px;
    h1 {
      font-size: clamp(37px, 8.7vw, 53px);
    }
  }
`;
export const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: 1.45fr 1fr;
  gap: 64px;
  align-items: center;
  @media (max-width: 850px) {
    grid-template-columns: 1fr;
    gap: 26px;
  }
`;
export const Intro = styled.p`
  max-width: 49ch;
  font-size: 17px;
  line-height: 1.7;
  color: var(--ink-dim);
`;
export const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px 24px;
  align-items: center;
  margin-top: 28px;
`;
export const ButtonLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  min-height: 48px;
  padding: 0 23px;
  border-radius: 4px;
  background: var(--ink);
  color: var(--bg);
  font-size: 14px;
  transition: background 160ms;
  &:hover {
    background: var(--accent);
  }
`;
export const TextLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  min-height: 44px;
  font-size: 13px;
  color: var(--ink);
  border-bottom: 1px solid transparent;
  &:hover {
    border-color: currentColor;
  }
`;
export const Employers = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px 34px;
  margin-top: 54px;
  padding-top: 24px;
  border-top: 1px solid var(--rule);
  span {
    font-size: 12px;
    color: var(--ink-mute);
  }
  img {
    height: 23px;
    max-width: 110px;
    opacity: 0.65;
    filter: grayscale(1);
  }
  @media (max-width: 700px) {
    margin-top: 34px;
    gap: 20px;
    img {
      height: 19px;
      max-width: 86px;
    }
    span {
      width: 100%;
    }
  }
`;
export const Section = styled.section`
  padding: 58px 0;
  scroll-margin-top: 24px;
  @media (max-width: 700px) {
    padding: 38px 0;
  }
`;
export const SectionHeading = styled.div`
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 28px;
  h2 {
    font-family: var(--font-body);
    font-weight: 550;
    font-size: clamp(27px, 3vw, 36px);
    line-height: 1.1;
    letter-spacing: -0.025em;
    color: var(--ink);
    margin-top: 10px;
  }
  > p {
    max-width: 35ch;
    font-size: 14px;
    line-height: 1.65;
  }
  @media (max-width: 700px) {
    display: block;
    > p {
      margin-top: 16px;
    }
  }
`;
export const WritingGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 36px;
  a {
    display: block;
    border-top: 1px solid var(--rule-strong);
    padding-top: 22px;
  }
  h3 {
    font-family: 'Instrument Serif', serif;
    font-size: 29px;
    color: var(--ink);
    margin: 12px 0;
  }
  p {
    font-size: 14px;
    line-height: 1.65;
    max-width: 44ch;
  }
  a:hover h3 {
    color: var(--accent);
  }
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;
export const Contact = styled.section`
  padding: 60px 0;
  margin-top: 36px;
  border-top: 1px solid var(--rule-strong);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 32px;
  h2 {
    font-family: 'Instrument Serif', serif;
    font-size: clamp(42px, 5vw, 64px);
    color: var(--ink);
    letter-spacing: -0.025em;
  }
  p {
    font-size: 14px;
    margin-top: 12px;
    line-height: 1.6;
  }
  @media (max-width: 700px) {
    display: block;
    padding: 38px 0;
  }
`;
export const Foot = styled.footer`
  border-top: 1px solid var(--rule);
  padding: 24px 0;
  ${Container} {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 12px;
  }
  p,
  a {
    font-size: 12px;
    color: var(--ink-mute);
    line-height: 1.7;
  }
  a:hover {
    color: var(--ink);
  }
`;
export const ProjectIntro = styled.div`
  padding: 30px 0;
  border-bottom: 1px solid var(--rule);
  ${Container} {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 20px;
    flex-wrap: wrap;
  }
  p {
    font-size: 13px;
    max-width: 62ch;
    line-height: 1.6;
  }
`;

export const HeroProof = styled.aside`
  padding: 28px;
  background: #202330;
  color: #f2f3f7;
  border-radius: 12px;
  box-shadow: 0 20px 50px #151d2815;
  position: relative;
  overflow: hidden;
  background-image: radial-gradient(ellipse at top right, #4b547c55, transparent 65%);
  ${Kicker} {
    color: #bfc6db;
    font-size: 10px;
  }
  .hero-number {
    display: flex;
    align-items: center;
    gap: 24px;
    margin: 20px 0;
  }
  .hero-number strong {
    font-size: 114px;
    font-weight: 500;
    letter-spacing: -0.08em;
    line-height: 1;
  }
  .hero-number span {
    font-size: 24px;
    line-height: 1.35;
    letter-spacing: -0.04em;
  }
  @media (max-width: 850px) {
    .hero-number strong {
      font-size: 82px;
    }
    .hero-number {
      margin: 12px 0;
    }
  }
`;
export const HeroTraction = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  border-top: 1px solid #ffffff22;
  padding-top: 12px;
  margin-top: 24px;
  strong {
    font-size: 30px;
    font-weight: 500;
    letter-spacing: -0.04em;
    color: #f2f3f7;
  }
  > div > span {
    display: block;
    font-size: 12px;
    line-height: 1.5;
    color: #bfc6db;
  }
`;
export const ProductMarks = styled.nav`
  display: flex;
  gap: clamp(3px, 1vw, 10px);
  flex-wrap: wrap;
`;
export const ProductMark = styled.a<{ $scale: number }>`
  position: relative;
  width: 44px;
  height: 48px;
  display: grid;
  place-items: center;
  border-radius: 10px;
  transition: transform 160ms ease, background-color 160ms ease;
  img {
    width: 38px;
    height: 38px;
    object-fit: contain;
    border-radius: 8px;
    transform: scale(${p => p.$scale});
  }
  span {
    position: absolute;
    top: calc(100% + 6px);
    left: 50%;
    transform: translateX(-50%);
    padding: 5px 8px;
    border: 1px solid #657086;
    border-radius: 4px;
    background: #f2f3f7;
    color: #202330;
    white-space: nowrap;
    font-size: 10px;
    opacity: 0;
    visibility: hidden;
    z-index: 2;
  }
  &:first-child span {
    left: 0;
    transform: none;
  }
  &:last-child span {
    left: auto;
    right: 0;
    transform: none;
  }
  &:hover,
  &:focus-visible {
    background: #ffffff12;
    transform: translateY(-3px);
  }
  &:hover span,
  &:focus-visible span {
    opacity: 1;
    visibility: visible;
  }
  &:focus-visible {
    outline: 2px solid #d4ccff;
    outline-offset: 3px;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:hover,
    &:focus-visible {
      transform: none;
    }
  }
`;
export const ImpactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 22px;
  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;
export const ImpactCard = styled.article<{ $dark?: boolean }>`
  --card-ink: ${p => (p.$dark ? '#f0f2f6' : '#252636')};
  --card-muted: ${p => (p.$dark ? '#b4bfca' : '#606572')};
  color: var(--card-ink);
  background: ${p => (p.$dark ? '#17332e' : '#eeedf3')};
  padding: 30px;
  border-radius: 10px;
  h3 {
    font-family: var(--font-body);
    font-size: clamp(21px, 2.3vw, 28px);
    line-height: 1.25;
    font-weight: 500;
    letter-spacing: -0.025em;
    margin: 24px 0;
  }
  > div:first-child img {
    filter: ${p => (p.$dark ? 'brightness(0) invert(1)' : 'none')};
  }
  > p {
    font-size: 13px;
    line-height: 1.75;
    color: var(--card-muted);
  }
  @media (max-width: 400px) {
    padding: 22px;
  }
`;
export const ImpactHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
  img {
    width: 60px;
    height: 25px;
    object-fit: contain;
  }
  span {
    font-size: 9px;
    font-family: var(--font-mono);
    letter-spacing: 0.04em;
    color: var(--card-muted);
  }
`;
export const Topology = styled.div`
  min-height: 95px;
  display: flex;
  align-content: center;
  flex-wrap: wrap;
  gap: 7px;
  align-items: center;
  span {
    background: #b5dec522;
    border: 1px solid #86b5a566;
    width: 35px;
    height: 28px;
    border-radius: 4px;
  }
  span:first-child:nth-last-child(2),
  span:last-child:nth-child(2) {
    width: calc(50% - 4px);
    height: 70px;
    display: grid;
    place-items: center;
    font-family: var(--font-mono);
    font-size: 11px;
    background: #a9d4bb;
    color: #17332e;
  }
`;
export const Controls = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  button {
    font-family: var(--font-body);
    font-size: 11px;
    min-height: 38px;
    padding: 8px 12px;
    border: 1px solid currentColor;
    border-radius: 5px;
    color: inherit;
    background: transparent;
    cursor: pointer;
  }
  button:hover {
    background: #88888818;
  }
  button[aria-pressed='true'] {
    background: #787e7b25;
    box-shadow: inset 0 -2px currentColor;
  }
  button:disabled {
    cursor: default;
    opacity: 0.7;
  }
`;
export const ImpactNumbers = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  padding: 24px 0 18px;
  margin-top: 20px;
  border-top: 1px solid #89939644;
  strong {
    display: block;
    font-size: clamp(26px, 3vw, 36px);
    font-weight: 500;
    letter-spacing: -0.045em;
  }
  span {
    display: block;
    font-size: 11px;
    margin-top: 5px;
    color: var(--card-muted);
  }
`;
export const Pipeline = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  height: 62px;
  span {
    background: #fff9;
    padding: 12px 8px;
    border: 1px solid #c5c5d3;
    flex: 1;
    text-align: center;
    border-radius: 4px;
    font-size: 11px;
  }
  b {
    font-weight: 400;
    color: #777584;
  }
`;
export const BarComparison = styled.div`
  display: grid;
  gap: 9px;
  margin-top: 10px;
  div {
    display: flex;
    gap: 12px;
    align-items: center;
  }
  span {
    font-size: 10px;
    width: 38px;
  }
  i {
    display: block;
    height: 10px;
    width: 74%;
    background: #cdc9da;
    border-radius: 2px;
  }
  div:last-child i {
    width: 29.6%;
    background: #7964bb;
  }
`;
export const CareerStrip = styled.div`
  margin-top: 34px;
  padding: 28px 0;
  border-top: 1px solid var(--rule);
  border-bottom: 1px solid var(--rule);
  > h3 {
    font-family: var(--font-body);
    font-size: clamp(21px, 2.3vw, 26px);
    font-weight: 500;
    line-height: 1.25;
    letter-spacing: -0.025em;
    color: var(--ink);
    margin-bottom: 24px;
  }
  > div {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 32px;
  }
  strong {
    display: block;
    font-size: 19px;
    font-weight: 500;
    color: var(--ink);
    margin: 10px 0;
    letter-spacing: -0.02em;
  }
  p:last-child {
    font-size: 13px;
    line-height: 1.6;
  }
  @media (max-width: 900px) {
    > div {
      grid-template-columns: 1fr;
      gap: 24px;
    }
  }
`;
export const ProductTabs = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 6px;
  margin-bottom: 26px;
  button {
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 16px 12px;
    min-height: 66px;
    border: 1px solid var(--rule);
    border-radius: 7px;
    background: transparent;
    color: var(--ink);
    font-size: 12px;
    cursor: pointer;
  }
  img {
    width: 27px;
    height: 27px;
    object-fit: contain;
    border-radius: 4px;
  }
  span {
    margin-left: auto;
    font-size: 9px;
    color: var(--ink-mute);
  }
  button[aria-selected='true'] {
    border-color: #6b6594;
    background: #ebe8f3;
    box-shadow: 0 2px 0 #6b6594;
  }
  @media (max-width: 850px) {
    grid-template-columns: repeat(3, 1fr);
    span {
      display: none;
    }
  }
  @media (max-width: 400px) {
    grid-template-columns: repeat(2, 1fr);
    button {
      padding: 10px;
    }
  }
`;
export const Showcase = styled.div`
  display: grid;
  grid-template-columns: 0.85fr 1.15fr;
  gap: 40px;
  align-items: center;
  min-height: 510px;
  align-items: start;
  @media (max-width: 850px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
  > div:last-child > p {
    min-height: 32px;
  }
`;
export const ProductCopy = styled.div`
  @media (max-width: 850px) {
    min-height: 385px;
  }
  @media (max-width: 650px) {
    min-height: 480px;
  }
  @media (max-width: 380px) {
    min-height: 565px;
  }

  h3 {
    font-family: var(--font-body);
    font-weight: 550;
    font-size: 31px;
    letter-spacing: -0.035em;
    line-height: 1.18;
    margin: 14px 0;
    color: var(--ink);
  }
  > p:not(:first-child) {
    font-size: 14px;
    line-height: 1.75;
  }
  > small {
    font-size: 12px;
    line-height: 1.6;
    display: block;
  }
`;
export const ProductMetric = styled.div`
  border-left: 3px solid #aca3c5;
  padding-left: 16px;
  margin: 22px 0 16px;
  strong {
    font-size: 33px;
    letter-spacing: -0.04em;
    color: var(--ink);
    font-weight: 500;
  }
  span {
    display: block;
    font-size: 12px;
    margin-top: 4px;
  }
`;
export const Demo = styled.div<{ $dark?: boolean }>`
  background: ${p => (p.$dark ? '#152c29' : '#f1ede7')};
  color: ${p => (p.$dark ? '#dce9e2' : '#393644')};
  border: 1px solid ${p => (p.$dark ? '#305148' : '#d8d2c8')};
  border-radius: 10px;
  overflow: hidden;
  min-height: 460px;
  @media (max-width: 850px) {
    min-height: 480px;
  }
  @media (max-width: 380px) {
    min-height: 510px;
  }
  box-shadow: 0 12px 35px #2020200b;
  > ${Controls} {
    padding: 0 20px 20px;
  }
`;
export const DemoBar = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding: 16px 20px;
  border-bottom: 1px solid #90909035;
  span {
    font-family: var(--font-mono);
    font-size: 10px;
  }
  span:last-child {
    opacity: 0.65;
  }
`;
export const DemoBody = styled.div`
  padding: 22px;
`;
export const DemoForm = styled.form`
  display: flex;
  background: #fffc;
  border: 1px solid #cfc7bf;
  border-radius: 6px;
  margin: 20px 0;
  overflow: hidden;
  input {
    min-width: 0;
    width: 100%;
    border: 0;
    background: transparent;
    padding: 14px 12px;
    color: #383441;
    font-size: 13px;
  }
  button {
    flex-shrink: 0;
    width: 44px;
    border: 0;
    background: #494052;
    color: white;
    cursor: pointer;
    font-size: 21px;
  }
`;
export const FileRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  font-size: 10px;
  color: #756b79;
  img {
    width: 34px;
    height: 34px;
    border-radius: 6px;
  }
`;
export const DemoResult = styled.div`
  min-height: 148px;
  padding: 8px 0 22px;
  > span {
    font-family: var(--font-mono);
    font-size: 9px;
    letter-spacing: 0.08em;
    color: #786784;
  }
  h4 {
    font-size: 23px;
    letter-spacing: -0.025em;
    line-height: 1.3;
    margin: 12px 0;
  }
  p {
    font-size: 12px;
    line-height: 1.6;
    color: #766d7b;
  }
  q {
    font-style: italic;
  }
`;
export const DemoCaption = styled.p`
  font-size: 10px !important;
  line-height: 1.6 !important;
  color: #7d7783;
  margin-top: 14px;
`;
export const MapWrap = styled.div`
  position: relative;
  height: 260px;
  svg {
    width: 100%;
    height: 100%;
  }
  circle {
    transition: opacity 180ms;
  }
  @media (prefers-reduced-motion: reduce) {
    circle {
      transition: none;
    }
  }
`;
export const MapCount = styled.div`
  position: absolute;
  bottom: 12px;
  left: 22px;
  pointer-events: none;
  background: #152c29dc;
  padding: 8px;
  strong {
    display: block;
    font-size: 30px;
    font-weight: 500;
    letter-spacing: -0.04em;
  }
  span {
    font-size: 10px;
  }
`;
export const TaskList = styled.div`
  display: grid;
  gap: 10px;
  margin-bottom: 18px;
  > div {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px;
    background: #fffa;
    border-radius: 5px;
    border: 1px solid #d8d2c8;
  }
  > div > span {
    color: #4e8c73;
  }
  strong {
    font-size: 14px;
    font-weight: 500;
  }
  small {
    display: block;
    margin-top: 4px;
    font-size: 10px;
    color: #7d7783;
  }
`;
export const LeadList = styled.div`
  margin-top: 18px;
  > div {
    border-top: 1px solid #ffffff25;
    padding: 14px 0;
  }
  span {
    font-size: 9px;
    display: block;
    color: #a5bbae;
  }
  strong {
    font-size: 14px;
    font-weight: 500;
    display: block;
    margin: 5px 0;
  }
  small {
    font-size: 11px;
    color: #b0c2b7;
  }
`;
export const StepTrack = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 22px;
  button {
    border: 1px solid #cabfaa;
    border-radius: 50%;
    width: 44px;
    height: 44px;
    background: #f7f4ef;
    color: #877344;
    cursor: pointer;
  }
  button[aria-pressed='true'] {
    background: #877344;
    color: white;
  }
`;

export const MetricDisclosure = styled.details`
  summary {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    min-height: 44px;
    list-style: none;
    cursor: pointer;
    border-radius: 3px;
    text-decoration-line: underline;
    text-decoration-style: dotted;
    text-decoration-color: #92949a;
    text-decoration-thickness: 1px;
    text-underline-offset: 6px;
  }
  summary::-webkit-details-marker {
    display: none;
  }
  summary:hover {
    text-decoration-style: solid;
  }
  summary:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 4px;
  }
`;
export const SourceIndicator = styled.span`
  font-family: var(--font-body);
  font-size: 12px;
  opacity: 0.7;
`;
export const SourceNote = styled.div`
  padding: 12px 0 6px;
  p {
    font-family: var(--font-body);
    font-size: 11px;
    line-height: 1.6;
    color: inherit;
    overflow-wrap: anywhere;
  }
  p + p {
    opacity: 0.75;
    margin-top: 6px;
  }
  a {
    display: inline-flex;
    align-items: center;
    min-height: 44px;
    font-size: 12px;
    text-decoration: underline;
    text-underline-offset: 3px;
  }
`;
